import {useMutation, useQuery} from "@tanstack/react-query";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {getDhiraaguJourneyOuValuesFromLeaf, isDhiraaguJourneyModuleConfiguration} from "~/domainModels/workspaceSpecific/dhiraagu";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {AnankeLabel} from "@vesta/ananke/components/anankeLabel";
import {AnankeSpinner} from "@vesta/ananke/components/anankeSpinner";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getEventContainersWithEvents, type EventContainerWithEvents} from "~/utilities/apis/api";
import {createEventContainer} from "~/utilities/apis/eventContainersApi";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {userHasAccessToOrganisationalUnit} from "~/utilities/accessControl/userOuAccess";
import {getUserDisplayName} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    getEventContainerDispositionColumnId,
} from "~/utilities/ottopilotSemanticColumns";

const PHONE_SEARCH_MIN_LENGTH = 5;
const SEARCH_DEBOUNCE_MS = 350;
const SEARCH_LIMIT = 10;

const FILTER_REVISION_ID = uniqueUuid`a1b2c3d4-e5f6-7890-abcd-ef1234567890`;
const SORT_REVISION_ID = uniqueUuid`b2c3d4e5-f6a7-8901-bcde-f12345678901`;

export type EventContainerSelectorPrefilledEventContainer = {
    eventContainerId: Uuid;
    phoneNumber: string;
};

export type EventContainerSelectorSelectParams = {
    eventContainer: EventContainerWithEvents;
    isNewlyCreated: boolean;
};

export type EventContainerSelectorProps = {
    selectedLeafOuId: Uuid;
    onEventContainerSelect: (params: EventContainerSelectorSelectParams) => void;
    isReadOnly: boolean;
    utmSource: string;
    leadOwner?: Uuid;
    utmPlatform: string;
    prefilledEventContainer?: EventContainerSelectorPrefilledEventContainer;
    phoneNumberColumnId: Uuid;
    utmSourceColumnId?: Uuid;
    utmPlatformColumnId?: Uuid;
    phoneInput?: string;
    onPhoneInputChange?: (value: string) => void;
    hideLabel?: boolean;
};

/**
 * Gets the IDs of leaf user templates from the user template hierarchy.
 * A leaf user template is one that no other template reports to
 * (i.e., no other template has this template's ID as its parentId).
 *
 * @param userTemplates - Array of user templates
 * @returns Set of user template IDs that are leaf nodes in the hierarchy
 */
export function getLeafUserTemplateIds(userTemplates: Array<UserTemplate>): Set<Uuid> {
    const templatesWithChildren = new Set<Uuid>();
    const templatesWithParent = new Set<Uuid>();

    for (const template of userTemplates) {
        if (template.parentId !== null) {
            templatesWithChildren.add(template.parentId);
            templatesWithParent.add(template.id);
        }
    }

    const leafTemplateIds = new Set<Uuid>();
    for (const template of userTemplates) {
        const hasChildren = templatesWithChildren.has(template.id);
        const hasParent = templatesWithParent.has(template.id);
        const isIsolated = !hasChildren && !hasParent;

        if (!hasChildren && !isIsolated) {
            leafTemplateIds.add(template.id);
        }
    }

    return leafTemplateIds;
}

/**
 * Checks if a user is a leaf user based on the user template hierarchy.
 *
 * @param user - The user to check
 * @param userTemplates - Array of user templates
 * @returns true if the user's template is a leaf node in the hierarchy
 */
export function isLeafUser(user: User, userTemplates: Array<UserTemplate>): boolean {
    const leafTemplateIds = getLeafUserTemplateIds(userTemplates);
    return leafTemplateIds.has(user.templateId);
}

/**
 * Round-robin user selection function.
 * Takes a list of users, sorts them alphabetically by display name,
 * finds the last assigned user, and returns the next user in the sorted list.
 *
 * @param users - Array of users to select from
 * @param userTemplates - Array of user templates for display name resolution
 * @param lastAssignedUserId - The ID of the last assigned user (from the most recent eventContainer)
 * @returns The next user in round-robin order, or the first user if lastAssignedUserId is not found
 */
export function getRoundRobinUser(users: Array<User>, userTemplates: Array<UserTemplate>, lastAssignedUserId: Uuid | null): User | null {
    if (users.length === 0) {
        return null;
    }

    const sortedUsers = [...users].sort((a, b) => {
        const nameA = getUserDisplayName(a.userId, users, userTemplates);
        const nameB = getUserDisplayName(b.userId, users, userTemplates);
        return nameA.localeCompare(nameB);
    });

    if (lastAssignedUserId === null) {
        return sortedUsers[0] ?? null;
    }

    const lastUserIndex = sortedUsers.findIndex((user) => user.userId === lastAssignedUserId);

    if (lastUserIndex === -1) {
        return sortedUsers[0] ?? null;
    }

    const nextIndex = (lastUserIndex + 1) % sortedUsers.length;
    return sortedUsers[nextIndex] ?? null;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedValue(value);
        }, delayMs);
        return () => {
            window.clearTimeout(timer);
        };
    }, [value, delayMs]);

    return debouncedValue;
}

export function EventContainerSelector(props: EventContainerSelectorProps) {
    const {
        selectedLeafOuId,
        onEventContainerSelect,
        isReadOnly,
        utmSource,
        utmPlatform,
        leadOwner,
        prefilledEventContainer,
        phoneNumberColumnId,
        utmSourceColumnId,
        utmPlatformColumnId,
        phoneInput: phoneInputProp,
        onPhoneInputChange,
        hideLabel,
    } = props;

    const {workspace} = useWorkspace();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const eventContainerTemplate = useOnlyEventContainerTemplate();
    const eventContainerTemplateConfiguration = eventContainerTemplate.configuration;
    const dispositionColumnId = getEventContainerDispositionColumnId(eventContainerTemplate);
    const organizationalUnitColumnId = eventContainerTemplate.configuration.organizationalUnitColumnConfig?.columnId ?? null;
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();

    const [internalPhoneInput, setInternalPhoneInput] = useState<string>(() => {
        if (prefilledEventContainer !== undefined && prefilledEventContainer !== null) {
            return prefilledEventContainer.phoneNumber;
        }
        return "";
    });
    const phoneInput = phoneInputProp ?? internalPhoneInput;
    const setPhoneInput = (value: string) => {
        setInternalPhoneInput(value);
        onPhoneInputChange?.(value);
    };
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [highlightedEventContainerId, setHighlightedJourneyId] = useState<Uuid | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const prefilledFiredRef = useRef(false);

    const debouncedPhoneInput = useDebouncedValue(phoneInput, SEARCH_DEBOUNCE_MS);

    const searchEnabled = debouncedPhoneInput.trim().length >= PHONE_SEARCH_MIN_LENGTH && isReadOnly !== true;

    const leafUsersWithOuAccess = useMemo(() => {
        return users.filter((user) => {
            const hasOuAccess = userHasAccessToOrganisationalUnit({
                targetUser: user,
                users: users,
                userTemplates: userTemplates,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                targetOrganisationalUnitId: selectedLeafOuId,
            });

            const isLeaf = isLeafUser(user, userTemplates);

            return hasOuAccess && isLeaf;
        });
    }, [users, userTemplates, organizationalUnits, organizationalUnitTemplates, selectedLeafOuId]);

    const lastEventContainerQuery = useQuery({
        queryKey: ["EventContainerSelector", "lastEventContainer", workspace.id, eventContainerTemplate.id, selectedLeafOuId],
        queryFn: async (): Promise<EventContainerWithEvents | null> => {
            if (organizationalUnitColumnId === null) {throw new Error("This OU-based selector does not support geography-only templates");}
            const result = await getEventContainersWithEvents({
                workspaceId: workspace.id,
                templateId: eventContainerTemplate.id,
                limit: 1,
                offset: 0,
                filterConfiguration: {
                    revisionId: FILTER_REVISION_ID,
                    lineItems: [
                        {
                            columnId: organizationalUnitColumnId,
                            operation: FilterOperation.equals,
                            value: selectedLeafOuId,
                        },
                    ],
                },
                sortConfiguration: {
                    revisionId: SORT_REVISION_ID,
                    lineItems: [
                        {
                            columnId: "createdAt",
                            direction: SortDirection.descending,
                        },
                    ],
                },
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (result.success !== true) {
                return null;
            }
            const eventContainers = result.data;
            if (eventContainers.length === 0) {
                return null;
            }
            return eventContainers[0] ?? null;
        },
        enabled: organizationalUnitColumnId !== null && leadOwner === undefined && isReadOnly !== true,
        retry: false,
        staleTime: 30_000,
    });

    const roundRobinLeadOwner = useMemo((): Uuid | null => {
        if (leadOwner !== undefined) {
            return leadOwner;
        }

        const leadOwnerColumnId = eventContainerTemplateConfiguration.eventContainersPage.leadOwnerColumnId;
        if (leadOwnerColumnId === undefined) {
            return null;
        }

        const lastEventContainer = lastEventContainerQuery.data;
        let lastAssignedUserId: Uuid | null = null;

        if (lastEventContainer !== null && lastEventContainer !== undefined) {
            const lastLeadOwner = lastEventContainer.additionalColumnValues[leadOwnerColumnId];
            if (typeof lastLeadOwner === "string" && lastLeadOwner !== "") {
                lastAssignedUserId = lastLeadOwner as Uuid;
            }
        }

        const nextUser = getRoundRobinUser(leafUsersWithOuAccess, userTemplates, lastAssignedUserId);
        return nextUser !== null ? nextUser.id : null;
    }, [leadOwner, eventContainerTemplateConfiguration.eventContainersPage.leadOwnerColumnId, lastEventContainerQuery.data, leafUsersWithOuAccess, userTemplates]);

    const searchQuery = useQuery({
        queryKey: ["EventContainerSelector", "search", workspace.id, eventContainerTemplate.id, debouncedPhoneInput.trim(), selectedLeafOuId, phoneNumberColumnId],
        queryFn: async (): Promise<Array<EventContainerWithEvents>> => {
            if (organizationalUnitColumnId === null) {throw new Error("This OU-based selector does not support geography-only templates");}
            const result = await getEventContainersWithEvents({
                workspaceId: workspace.id,
                templateId: eventContainerTemplate.id,
                limit: SEARCH_LIMIT,
                offset: 0,
                filterConfiguration: {
                    revisionId: FILTER_REVISION_ID,
                    lineItems: [
                        {
                            columnId: phoneNumberColumnId,
                            operation: FilterOperation.contains,
                            value: debouncedPhoneInput.trim(),
                        },
                        {
                            columnId: organizationalUnitColumnId,
                            operation: FilterOperation.equals,
                            value: selectedLeafOuId,
                        },
                    ],
                },
                sortConfiguration: {
                    revisionId: SORT_REVISION_ID,
                    lineItems: [
                        {
                            columnId: "updatedAt",
                            direction: SortDirection.descending,
                        },
                    ],
                },
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        enabled: organizationalUnitColumnId !== null && searchEnabled,
        retry: false,
        staleTime: 30_000,
    });

    const searchResults = searchQuery.data ?? [];
    const isSearching = searchQuery.isFetching;
    const hasSearched = searchEnabled && searchQuery.isFetched;
    const showNoResults = hasSearched && searchResults.length === 0 && debouncedPhoneInput.trim().length > 0;
    const hasFiveDigits = debouncedPhoneInput.trim().length >= PHONE_SEARCH_MIN_LENGTH;

    const prefilledQuery = useQuery({
        queryKey: ["EventContainerSelector", "prefilled", eventContainerTemplate.id, prefilledEventContainer?.eventContainerId],
        queryFn: async (): Promise<EventContainerWithEvents | null> => {
            if (prefilledEventContainer === undefined || prefilledEventContainer === null) {
                return null;
            }
            const result = await getEventContainersWithEvents({
                workspaceId: workspace.id,
                templateId: eventContainerTemplate.id,
                limit: 1,
                offset: 0,
                filterConfiguration: {
                    revisionId: FILTER_REVISION_ID,
                    lineItems: [
                        {
                            columnId: "id",
                            operation: FilterOperation.equals,
                            value: prefilledEventContainer.eventContainerId,
                        },
                    ],
                },
                sortConfiguration: {
                    revisionId: SORT_REVISION_ID,
                    lineItems: [],
                },
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (result.success !== true) {
                throw result.error;
            }
            const eventContainers = result.data;
            if (eventContainers.length === 0) {
                return null;
            }
            return eventContainers[0] ?? null;
        },
        enabled: prefilledEventContainer !== undefined && prefilledEventContainer !== null && prefilledFiredRef.current === false,
        retry: false,
    });

    useEffect(() => {
        if (prefilledQuery.data === undefined || prefilledQuery.data === null || prefilledFiredRef.current === true) {
            return;
        }
        prefilledFiredRef.current = true;
        onEventContainerSelect({eventContainer: prefilledQuery.data, isNewlyCreated: false});
    }, [prefilledQuery.data, onEventContainerSelect]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchEventContainerById = useCallback(
        async (eventContainerId: Uuid): Promise<EventContainerWithEvents | null> => {
            const result = await getEventContainersWithEvents({
                workspaceId: workspace.id,
                templateId: eventContainerTemplate.id,
                limit: 1,
                offset: 0,
                filterConfiguration: {
                    revisionId: FILTER_REVISION_ID,
                    lineItems: [
                        {
                            columnId: "id",
                            operation: FilterOperation.equals,
                            value: eventContainerId,
                        },
                    ],
                },
                sortConfiguration: {
                    revisionId: SORT_REVISION_ID,
                    lineItems: [],
                },
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (result.success !== true) {
                return null;
            }
            const eventContainers = result.data;
            if (eventContainers.length === 0) {
                return null;
            }
            return eventContainers[0] ?? null;
        },
        [workspace.id, eventContainerTemplate.id, intellsysBaseUrl, heimdallUser.accessToken],
    );

    const createEventContainerMutation = useMutation({
        mutationKey: ["EventContainerSelector", "createEventContainer"],
        mutationFn: async (phone: string): Promise<EventContainerWithEvents> => {
            if (organizationalUnitColumnId === null) {throw new Error("This OU-based selector does not support geography-only templates");}
            const trimmedPhone = phone.trim();
            const additionalColumnValues: Record<Uuid, unknown> = {
                [dispositionColumnId]: eventContainerTemplateConfiguration.dispositionColumnConfig.defaultValue,
                [organizationalUnitColumnId]: selectedLeafOuId,
                [phoneNumberColumnId]: trimmedPhone,
            };
            if (utmSourceColumnId !== undefined && utmSource.trim() !== "") {
                additionalColumnValues[utmSourceColumnId] = utmSource.trim();
            }
            if (utmPlatformColumnId !== undefined && utmPlatform.trim() !== "") {
                additionalColumnValues[utmPlatformColumnId] = utmPlatform.trim();
            }

            if (eventContainerTemplateConfiguration.eventContainersPage.leadOwnerColumnId !== undefined && roundRobinLeadOwner !== null) {
                additionalColumnValues[eventContainerTemplateConfiguration.eventContainersPage.leadOwnerColumnId] = roundRobinLeadOwner;
            }

            const statusColumnConfig = eventContainerTemplateConfiguration.statusColumnConfig;
            if (statusColumnConfig !== undefined && statusColumnConfig.defaultStatusValue !== undefined) {
                additionalColumnValues[statusColumnConfig.columnId] = statusColumnConfig.defaultStatusValue;
            }

            if (isDhiraaguJourneyModuleConfiguration(eventContainerTemplateConfiguration)) {
                const ouValues = getDhiraaguJourneyOuValuesFromLeaf(selectedLeafOuId, organizationalUnits, organizationalUnitTemplates);
                for (const [columnId, value] of Object.entries(ouValues)) {
                    additionalColumnValues[columnId as Uuid] = value;
                }
            }

            const eventContainerIdResult = await createEventContainer({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                data: {
                    ...additionalColumnValues,
                },
                templateId: eventContainerTemplate.id,
                workspaceId: workspace.id,
            });
            if (eventContainerIdResult.success !== true) {
                throw eventContainerIdResult.error;
            }
            const newEventContainerId = eventContainerIdResult.data;

            const eventContainer = await fetchEventContainerById(newEventContainerId);
            if (eventContainer === null) {
                throw new Error("Failed to fetch created eventContainer");
            }
            return eventContainer;
        },
    });

    const handleSelectEventContainer = useCallback(
        async (eventContainer: EventContainerWithEvents) => {
            const full = await fetchEventContainerById(eventContainer.id);
            if (full === null) {
                return;
            }
            setIsDropdownOpen(false);
            onEventContainerSelect({eventContainer: full, isNewlyCreated: false});
        },
        [fetchEventContainerById, onEventContainerSelect],
    );

    const handleAddNew = useCallback(() => {
        const trimmed = phoneInput.trim();
        if (trimmed.length === 0) {
            return;
        }
        createEventContainerMutation.mutate(trimmed, {
            onSuccess: (eventContainer) => {
                setIsDropdownOpen(false);
                onEventContainerSelect({eventContainer: eventContainer, isNewlyCreated: true});
            },
        });
    }, [phoneInput, createEventContainerMutation, onEventContainerSelect]);

    const getEventContainerDisplayLabel = useCallback(
        (eventContainer: EventContainerWithEvents): string => {
            const phoneValue = eventContainer.additionalColumnValues[phoneNumberColumnId];
            if (typeof phoneValue === "string" && phoneValue !== "") {
                return phoneValue;
            }
            return processOttopilotStringTemplate(eventContainerTemplateConfiguration.displayNameTemplate, eventContainer.additionalColumnValues);
        },
        [phoneNumberColumnId, eventContainerTemplateConfiguration.displayNameTemplate],
    );

    const handleInputFocus = useCallback(() => {
        if (isReadOnly !== true) {
            setIsDropdownOpen(true);
        }
    }, [isReadOnly]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneInput(e.target.value);
        setIsDropdownOpen(true);
    }, []);

    if (organizationalUnitColumnId === null) {
        return <div role="status">This OU-based selector is unavailable for geography-only leads.</div>;
    }

    if (isReadOnly === true) {
        return (
            <div className="w-full">
                <AnankeLabel isRequired={true}>Contact number</AnankeLabel>
                <AnankeTextField
                    value={phoneInput}
                    isReadOnly={true}
                    isDisabled={true}
                />
            </div>
        );
    }

    return (
        <div
            className="w-full relative"
            ref={containerRef}
        >
            {hideLabel !== true && <AnankeLabel isRequired={true}>Contact number</AnankeLabel>}
            <AnankeTextField
                value={phoneInput}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder=""
            />

            {isDropdownOpen === true && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded border border-ds-border bg-ds-surface-overlay text-ds-text shadow-lg">
                    <div className="px-3 py-2 text-xs font-semibold text-ds-text-subtle uppercase tracking-wide border-b border-ds-border bg-ds-surface-overlay">
                        Customers
                    </div>
                    <div className="max-h-48 overflow-y-auto bg-ds-surface-overlay">
                        {hasFiveDigits !== true ? (
                            <div className="px-3 py-3 text-ds-text-subtlest text-sm">Please enter at least 5 digits to view results</div>
                        ) : isSearching === true ? (
                            <div className="px-3 py-3 flex items-center gap-2 text-ds-text-subtlest text-sm">
                                <span>Loading</span>
                                <AnankeSpinner size="small" />
                            </div>
                        ) : showNoResults === true ? (
                            <div className="px-3 py-2">
                                <p className="text-ds-text-subtlest text-sm mb-2">No customers available</p>
                                <div className="flex items-center justify-between">
                                    <span className="text-ds-text text-sm">{phoneInput.trim()}</span>
                                    <button
                                        type="button"
                                        className="text-ds-link text-sm font-medium hover:underline disabled:opacity-50"
                                        onClick={handleAddNew}
                                        disabled={createEventContainerMutation.isPending === true}
                                    >
                                        {createEventContainerMutation.isPending === true ? "Adding..." : "Add"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <ul>
                                {searchResults.map((eventContainer) => {
                                    const isHighlighted = highlightedEventContainerId === eventContainer.id;
                                    return (
                                        <li key={eventContainer.id}>
                                            <button
                                                type="button"
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                                    isHighlighted === true ? "bg-ds-background-selected" : "hover:bg-ds-background-neutral-hovered"
                                                }`}
                                                onClick={() => handleSelectEventContainer(eventContainer)}
                                                onMouseEnter={() => setHighlightedJourneyId(eventContainer.id)}
                                                onMouseLeave={() => setHighlightedJourneyId(null)}
                                            >
                                                {getEventContainerDisplayLabel(eventContainer)}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
